import {message, Select, Skeleton, Spin} from 'antd';
import compact from 'lodash/compact';
import find from 'lodash/find';
import compose from 'lodash/fp/compose';
import filterFP from 'lodash/fp/filter';
import getFP from 'lodash/fp/get';
import mapFP from 'lodash/fp/map';
import uniqByFP from 'lodash/fp/uniqBy';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import map from 'lodash/map';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import useSearchValue from '@/hooks/useSearchValue';
import {createMapEntityToOptions} from '@/utils/createMapEntityToOptions';
import findMatch from '@/utils/findMatch';
import {useFindValue} from '@/utils/useFindValue';

const defaultGetValue = (value, keyValue) => isObject(value) ? value[keyValue] : value;

export const createSearchFieldRemote = (
  {
    keyValue = '_id',
    keyLabel = 'name',
    usePagination,
    getValue = defaultGetValue,
    mapResponse = v => v,
    fullEntity = false,
    customMapFunc = v => v,
    fetchValueFunc,
    loadValue = true,
    size = 250,
    ...rest
  }
) => {

  const FieldSearchRemote = ({value, onChange, onChangeRef, filters, ...props}) => {
    const {t} = useTranslation('alert');
    const [searchValue, setSearchValue] = useSearchValue('');

    const {data: res = [], isLoading} = usePagination(searchValue, filters, {size});
    const {isLoading: valueLoading, data: customValue, isError} = useFindValue(fetchValueFunc, value, loadValue);

    const _value = useMemo(() => {
      if (customValue) {
        return isArray(customValue)
          ? compose(
            mapFP(v => getValue(v, keyValue)),
            uniqByFP(keyValue),
          )(customValue)
          : getValue(customValue, keyValue);
      }
    }, [customValue]);

    const data = useMemo(() => {
      if (!searchValue) {
        if (customValue) {
          const exist = res && res.some((e) => e._id === customValue?.id);
          if (!exist && typeof customValue !== 'string')
            res.unshift(customValue);
        }
      }
      return res.map(e => e);
    }, [customValue, searchValue, res]);

    const options = compose(
      uniqByFP('value'),
      filterFP(getFP('value')),
      compact,
      mapFP(customMapFunc),
      mapFP(createMapEntityToOptions({keyValue, keyLabel})),
      d => {
        if (isArray(value)) {
          return [...value, ...d];
        } else if (isObject(value)) {
          return [value, ...d];
        } else {
          return d;
        }
      },
    )(mapResponse(data));

    const handleChange = useCallback((_value) => {
      let fullItem;
      if (isArray(value)) {
        fullItem = map(_value, v => find(data, {[keyValue]: v}));
      } else {
        fullItem = find(data, {[keyValue]: _value});
      }
      onChange && onChange(fullEntity ? fullItem : _value);
      onChangeRef && onChangeRef(fullItem);
    }, [value, onChange, onChangeRef, data]);

    const handleFilter = useCallback((input, option) => {
      return findMatch(input, option?.label);
    }, []);

    useEffect(() => {
      if (isError)
        message.error(t('error.NOTFOUNDERROR.415'));
    }, [isError, t]);

    if (valueLoading)
      return <Skeleton paragraph={false} active title={{className: 'm-0', width: '100%'}}/>;

    return (
      <Select
        value={_value}
        onChange={handleChange}
        className="w-full"
        loading={isLoading || valueLoading}
        options={options}
        optionFilterProp={keyLabel}
        showSearch
        onSearch={setSearchValue}
        filterOption={handleFilter}
        status={isError ? 'error' : null}
        notFoundContent={isLoading || valueLoading ? <Spin size="small"/> : undefined}
        {...rest}
        {...props}
      />
    );
  };

  FieldSearchRemote.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func,
    tagRender: PropTypes.func,
    onChangeRef: PropTypes.func,
    filters: PropTypes.any,
  };

  FieldSearchRemote.defaultProps = {
    loadValue: true
  };

  return memo(FieldSearchRemote);
};
